import * as CONSTANTS from './constants'
import { Notification } from './Notification';
import { NotificationInstance } from './NotificationInstance';
import { loadFromTsv } from './Tsv';

export class NotificationManager {
    notificationsByStatus;

    constructor() {}

    static async build(filename) {
        let instance = new NotificationManager();
        let headerLines = 5;
        let notifications = await loadFromTsv(filename, headerLines, function(row, lineNum) {
            return NotificationManager.notificationTsvTransformer(row, lineNum);
        });
        instance.notificationsByStatus = instance.sortNotificationByStatus(notifications);
        return instance;
    }

    static notificationTsvTransformer(row) {
        let statuses = row["STATUSES"] === "infected" ? [CONSTANTS.INFECTED_SPREAD, CONSTANTS.INFECTED_DIRECTED] : [row["STATUSES"]];
        let devices = row["DEVICE_TYPES"] === "all" ? CONSTANTS.ALL_DEVICE_TYPES : [row["DEVICE_TYPES"]];
        let groups = row["GROUPS"] === "all" ? CONSTANTS.ALL_GROUPS : [row["GROUPS"]];
        let names = row["NAMES"] === "" ? [] : [row["NAMES"]];
        let roles = row["ROLES"] === "all" ? CONSTANTS.ALL_ROLES : [row["ROLES"]];
        let message = row["MESSAGE"].trim();
        return new Notification(message, devices, groups, statuses, roles, names);
    }

    updateAllNotifications(storage, round) {
        for (let nodeName of storage.getNodeNames()) {
            this.addNotificationToNode(nodeName, round, storage);
        }
    }

    // selects a number of notification per role equal to count that relate to the node and adds it to the database
    addNotificationToNode(nodeName, round, storage, count = 1, messages = []) {
        let node = storage.getNodeData(nodeName);
        if (count !== 1 && messages.length !== 0 && count !== messages.length) {
            throw new Error("cannot provide both count and messages");
        }
        let notesByGroup = [];
        if (node.isOff() && messages.length === 0) {
            return;
        }
        for (let role of CONSTANTS.ALL_ROLES) {
            let visibleStatus = node.getVisibleStatus(role, true);
            if (messages.length > 0) {
                let roleNotes = [];
                for (let message of messages) {
                    roleNotes.push(this.createNotificationInstance(message, round))
                }
                notesByGroup[role] = roleNotes;
            }
            else {
                let roleNotes = [];
                for (let i = 0; i < count; i++) {
                    roleNotes.push(this.getRandomMatchingNotification(node, visibleStatus, role, round, storage))
                }
                notesByGroup[role] = roleNotes;
            }
        }
        storage.updateNodeNotifications(node.getName(), notesByGroup)
    }

    createNotificationInstance(message, round) {
        return new NotificationInstance(message, this.getCurrentTimestamp(), round);
    }

    sortNotificationByStatus(notifications) {
        let d = [];
        for (let notification of notifications) {
            for (let status of notification.getStatuses()) {
                if (status in d) {
                    d[status].push(notification);
                }
                else {
                    d[status] = [notification];
                }
            }
        }
        return d;
    }

    addOffNotificationToNode(storage, node, round) {
        let notification = "This device has been turned off.";
        this.addNotificationToNode(node, round, storage, 1, [notification]);
    }

    addEdgeDisconnectionNotificationToNodes(storage, src, dst, round) {
        let srcNotification = `${dst} has been disconnected`;
        let dstNotification = `${src} has been disconnected`;
        this.addNotificationToNode(src, round, storage, 1, [srcNotification]);
        this.addNotificationToNode(dst, round, storage, 1, [dstNotification]);
    }

    getRandomMatchingNotification(node, visibleStatus, role, round, storage) {
        let matchingMessages = [];
        let lookupStatus = (node.isInvestigated() || visibleStatus === CONSTANTS.COMPROMISED) ? node.getStatus() : visibleStatus;
        if (!Object.keys(this.notificationsByStatus).includes(lookupStatus)) {
            return new NotificationInstance(`no notifications found for status category: ${lookupStatus}`, this.getCurrentTimestamp, round);
        }
        for (let note of this.notificationsByStatus[lookupStatus]) {
            if (note.getDevices().includes(node.getType()) && note.getGroups().includes(node.getGroup()) && note.getRoles().includes(role)) {
                // if multiple notifications exist for the same node name and status, only the first will be used
                // TODO notification.name is also being used for to specify group-specific messages, change naming
                if (note.getNames().includes(node.getName()) || note.getNames().includes(node.getGroup())) {
                    return this.createNotificationInstance(note.message, round);
                } // if this notification has a specific group/name but it wasn't this nodes's group/name, don't use it 
                if (note.getNames().length > 0 && note.getNames()[0] !== "") continue;
                matchingMessages.push(this.createNotificationInstance(note.getMessage(), round));
            }
        }

        if (matchingMessages.length === 0) {
            return this.createNotificationInstance(`no notifications found for node ${node.getname()} with status ${lookupStatus}`, round);
        }
        return matchingMessages[Math.floor(Math.random() * matchingMessages.length)];
    }

    toJSON() {
        return {
            notifications_by_status: {
                [CONSTANTS.HEALTHY]: this.notificationsByStatus[CONSTANTS.HEALTHY],
                [CONSTANTS.OFF]: this.notificationsByStatus[CONSTANTS.OFF],
                [CONSTANTS.UNKNOWN]: this.notificationsByStatus[CONSTANTS.UNKNOWN],
                [CONSTANTS.INFECTED_DIRECTED]: this.notificationsByStatus[CONSTANTS.INFECTED_DIRECTED],
                [CONSTANTS.INFECTED_SPREAD]: this.notificationsByStatus[CONSTANTS.INFECTED_SPREAD]
            }
        };
    }

    static jsonDeserialize(jsonString) {
        let instance = new NotificationManager();
        let data = jsonString;
        let notificationsByStatus = data.notifications_by_status;
        instance.notificationsByStatus = {
            [CONSTANTS.HEALTHY]: notificationsByStatus[CONSTANTS.HEALTHY].map(json => {
                return Notification.jsonDeserialize(json);
            }),
            [CONSTANTS.OFF]: notificationsByStatus[CONSTANTS.OFF].map(json => {
                return Notification.jsonDeserialize(json);
            }),
            [CONSTANTS.UNKNOWN]: notificationsByStatus[CONSTANTS.UNKNOWN].map(json => {
                return Notification.jsonDeserialize(json);
            })
        }
        if (CONSTANTS.INFECTED_DIRECTED in notificationsByStatus) {
            instance.notificationsByStatus[CONSTANTS.INFECTED_DIRECTED] = notificationsByStatus[CONSTANTS.INFECTED_DIRECTED].map(json => {
                return Notification.jsonDeserialize(json);
            })
        }
        if (CONSTANTS.INFECTED_SPREAD in notificationsByStatus) {
            instance.notificationsByStatus[CONSTANTS.INFECTED_SPREAD] = notificationsByStatus[CONSTANTS.INFECTED_SPREAD].map(json => {
                return Notification.jsonDeserialize(json);
            })
        }
        return instance;
    }

    getCurrentTimestamp() {
        return Date.now()
    }
}